import { Button, ButtonProps, forwardRef } from '@apollo/orbit';
import React from 'react';
import { Link } from 'react-router-dom';

import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useUpgradeContext } from 'src/hooks/useUpgradeContext';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { PermissionWarning } from '../permissionGuards/permissionWarning/PermissionWarning';

interface PermissionedButtonProps extends Props {
  hasPermission: boolean;
  kind: GraphQLTypes.BillingPlanKind | undefined;
  ref?: HTMLElement | null;
}

const PermissionedButton = forwardRef<PermissionedButtonProps, 'button'>(
  (props, ref) => {
    const {
      hasPermission,
      variant = 'secondary',
      className,
      kind,
      children,
    } = props;
    const { upgradeRoute, upgradeButtonText } = useUpgradeContext();

    return (
      <Button
        variant={variant}
        className={className}
        isDisabled={!hasPermission}
        as={Link}
        to={upgradeRoute}
        data-analytics-category="Upgrade Account"
        data-analytics-label={`Clicked to upgrade account from ${kind}`}
        ref={ref}
      >
        {children || upgradeButtonText}
      </Button>
    );
  },
);

interface Props extends Omit<ButtonProps, 'ref'> {
  accountId: string;
  children?: React.ReactNode;
}

/**
 *
 * @param accountId account id for an org
 *
 * @returns React.Node (Button or ClickableText)
 */
export const UpgradeButton = ({ accountId, ...rest }: Props) => {
  const currentPlanResults = useCurrentPlan({
    accountId,
  }).currentPlan;
  // TODO(scale): Add a case here for Standard when we update the Scale plan kinds
  const canUpgrade =
    currentPlanResults?.tier !== GraphQLTypes.BillingPlanTier.ENTERPRISE;

  return canUpgrade ? (
    <PermissionWarning
      accountId={accountId}
      accountPermissions={['canSetConstrainedPlan']}
      childRenderer={(hasPermission) => (
        <PermissionedButton
          {...rest}
          kind={currentPlanResults?.kind}
          accountId={accountId}
          hasPermission={hasPermission}
        />
      )}
    />
  ) : null;
};
